import axios from 'axios'

const RESET = 'RESET'
const SET_CURRENT_MONITORING_FEATURE = 'SET_CURRENT_MONITORING_FEATURE'
const ADD_MONITORING_FEATURE = 'ADD_MONITORING_FEATURE'
const REMOVE_MONITORING_FEATURE = 'REMOVE_MONITORING_FEATURE'
const UPDATE_CURRENT_MONITORING_FEATURE = 'UPDATE_CURRENT_MONITORING_FEATURE'
const UPDATE_MONITORING_FEATURE = 'UPDATE_MONITORING_FEATURE'
const SET_MONITORING_FEATURES = 'SET_MONITORING_FEATURES'
const SET_FEATURE_DATA_SOURCES = 'SET_FEATURE_DATA_SOURCES'

const BASE_URL = 'api/MonitoringFeatures'
const CREATE_MONITORING_FEATURE_URL = 'api/MonitoringFeatures'
const UPDATE_MONITORING_FEATURE_URL = 'api/MonitoringFeatures'
const REMOVE_MONITORING_FEATURE_URL = (id) => `api/MonitoringFeatures/${id}`
const FETCH_MONITORING_FEATURE_URL = (id) => `${BASE_URL}/${id}`
const FETCH_MONITORING_FEATURE_DETAILS_URL = (id) => `${BASE_URL}/${id}/Details`

const GET_STATION_DASHBOARD_PRECONFIG_URL = (stationId) => `api/QueryConfiguration/StationDashboard/${stationId}`

const initialState = () => ({
  monitoringFeatures: [],
  featureDataSources: [],
  currentMonitoringFeature: null,
})

const STATION_NOT_FOUND = 'STATION_NOT_FOUND'
const INVALID_LOCATION = 'NO_LOCATION_ASSIGNED'
const LOCATION_NOT_FOUND = 'LOCATION_NOT_FOUND'
const NO_LOCATION_NAME = 'NO_LOCATION_NAME'

export const SOURCE_TYPES = {
  continuousAquarius: 'continuous_aquarius',
  discreteEnvirodata: 'discrete_envirodata',
  vegetativeMonitoringEnvirodata: 'vegetativemonitoring_envirodata',
  surveysEnvirodata: 'surveys_envirodata',
}

// Show these source types on the map.
export const SOURCE_TYPES_ON_MAP = [SOURCE_TYPES.discreteEnvirodata, SOURCE_TYPES.continuousAquarius, SOURCE_TYPES.vegetativeMonitoringEnvirodata, SOURCE_TYPES.surveysEnvirodata]

// These source types can be used for a monitoring location name.
export const SOURCE_TYPES_WITH_NAME = [{
  key: SOURCE_TYPES.discreteEnvirodata,
  singularName: 'Discrete Station',
  dataType: 'Discrete Data',
}, {
  key: SOURCE_TYPES.continuousAquarius,
  singularName: 'Continuous Station',
  dataType: 'Continuous Data',
}, {
  key: SOURCE_TYPES.vegetativeMonitoringEnvirodata,
  singularName: 'Vegetative Plot',
  dataType: 'Vegetative Data',
},{
  key: SOURCE_TYPES.surveysEnvirodata,
  singularName: 'Survey Location',
  dataType: 'Survey Data',
}]

let sourceTypesWithNameIndex = {}
SOURCE_TYPES_WITH_NAME.forEach(sourceType => sourceTypesWithNameIndex[sourceType.key] = sourceType)
Object.freeze(sourceTypesWithNameIndex)
export const SOURCE_TYPES_WITH_NAME_INDEX = sourceTypesWithNameIndex

export const SOURCE_TYPES_WITH_METADATA = [SOURCE_TYPES.discreteEnvirodata]

/* Begin detail filters */
// Used to filter data by specific data types 
export const SOURCE_TYPES_DATA_TYPES = {
  discreteEnvirodata: {
    wqData: 'wqData',
    metadata: 'metadata',
    sampleActivities: 'sampleActivities',
    tenantFlags: 'tenantFlags',
  },
  continuousAquarius: {

  },
}

const ELEMENT_DELIMITER = ','
const LIST_DELIMITER = '.'

function makeDetailFilterParams(sourceTypes, sourceTypeProperties) {
  return {
    sourceTypes: sourceTypes && sourceTypes.join(ELEMENT_DELIMITER),
    sourceTypeProperties: sourceTypeProperties && sourceTypeProperties.map(stype => stype.join(ELEMENT_DELIMITER)).join(LIST_DELIMITER),
  }
}
/* End detail filters */

export default {
  namespaced: true,
  state: initialState(),
  actions: {
    async fetchMonitoringFeature(_, {
      monitoringFeatureId,
    }) {
      try {
        let response = await axios.get(FETCH_MONITORING_FEATURE_URL(monitoringFeatureId))
        return response.data
      } catch (error) {
        console.error(error)
        throw error.response.data
      }
    },
    async fetchMonitoringFeatureDetails(_, {
      monitoringFeatureId,
      sourceTypes,
      sourceTypeProperties,
    }) {
      try {
        let response = await axios.get(FETCH_MONITORING_FEATURE_DETAILS_URL(monitoringFeatureId), {
          params: {
            ...makeDetailFilterParams(sourceTypes, sourceTypeProperties),
          },
        })
        return response.data
      } catch (error) {
        console.error(error)
        throw error.response.data
      }
    },
    async createMonitoringFeature({
      commit,
    }, {
      monitoringFeature,
    }) {
      try {
        let response = await axios.post(CREATE_MONITORING_FEATURE_URL, monitoringFeature)
        let mf = response.data
        commit(ADD_MONITORING_FEATURE, mf)

        return mf
      } catch (error) {
        console.error(error)
        throw error.response.data
      }
    },
    async removeMonitoringFeature({
      commit,
    }, {
      monitoringFeature,
    }) {
      try {
        await axios.delete(REMOVE_MONITORING_FEATURE_URL(monitoringFeature.Id))
        commit(REMOVE_MONITORING_FEATURE, monitoringFeature)
      } catch (error) {
        console.error(error)
        throw error.response.data.Message
      }
    },
    async refreshCurrentMF({
      commit,
      state,
      dispatch,
    }) {
      try {
        if (!state.currentMonitoringFeature) return
        let monitoringFeatureId = state.currentMonitoringFeature.Id
        let monitoringFeature = await dispatch('fetchMonitoringFeature', {
          monitoringFeatureId,
        })

        monitoringFeature = {
          ...state.currentMonitoringFeature,
          ...monitoringFeature,
        }
        commit(SET_CURRENT_MONITORING_FEATURE, {
          monitoringFeature,
        })
      } catch (error) {
        console.error(error)
        throw error.response.data
      }
    },
    async refreshCurrentMFDetails({
      commit,
      getters,
      dispatch,
    }, {
      sourceTypes,
      sourceTypeProperties,
    } = {}) {
      if (!getters.currentMonitoringFeature) return
      let monitoringFeature = JSON.parse(JSON.stringify(getters.currentMonitoringFeature))

      let monitoringFeatureDetails = await dispatch('fetchMonitoringFeatureDetails', {
        monitoringFeatureId: monitoringFeature.Id,
        sourceTypes,
        sourceTypeProperties,
      })

      monitoringFeature.FeatureDataSources = monitoringFeature.FeatureDataSources.map(fds => {
        let newDetails = monitoringFeatureDetails.FeatureDataSources.find(newFds => newFds.SourceType == fds.SourceType && newFds.SourceId == fds.SourceId)
        if (!newDetails) return fds

        // Merge the updated data into the existing data. 
        return {
          ...fds,
          ...newDetails,
          // Since filter can update a specific data type, don't want subset to overwrite the whole set of data.
          Data: {
            ...fds.Data,
            ...newDetails.Data,
          },
        }
      })

      commit(UPDATE_CURRENT_MONITORING_FEATURE, monitoringFeature)
    },
    setCurrentMonitoringFeature({
      commit,
      getters,
    }, {
      monitoringFeatureId,
      monitoringFeatureDetails,
    }) {
      let monitoringFeature = getters.monitoringFeaturesIndex[monitoringFeatureId]

      commit(SET_CURRENT_MONITORING_FEATURE, {
        monitoringFeature,
        monitoringFeatureDetails,
      })
    },
    async fetchMonitoringFeaturesUI({
      commit,
    }) {
      try {
        let response = await axios.get('api/MonitoringFeaturesUI')
        let {
          MonitoringFeatures,
          FeatureDataSources,
        } = response.data

        commit(SET_MONITORING_FEATURES, MonitoringFeatures)
        commit(SET_FEATURE_DATA_SOURCES, FeatureDataSources)
      } catch (error) {
        console.error(error)
        throw error.response.data
      }
    },
    async updateMonitoringFeature({
      commit,
    }, {
      monitoringFeature = {},
      data,
    }) {
      try {
        // Strip extra data for a smaller request
        const params = {
          Id: monitoringFeature.Id,
          DisplayName: monitoringFeature.DisplayName,
          ...data,
        }

        await axios.put(UPDATE_MONITORING_FEATURE_URL, params)

        commit(UPDATE_CURRENT_MONITORING_FEATURE, params)
        commit(UPDATE_MONITORING_FEATURE, params)
      } catch (error) {
        console.error(error)
        throw error.response.data.Message
      }
    },
    async getStationDashboardPreConfig({
      commit,
    }, {
      stationId,
    }) {
      let url = GET_STATION_DASHBOARD_PRECONFIG_URL(stationId)
      var res = await axios.get(url)
      
      return res.data
    },
  },
  mutations: {
    [SET_MONITORING_FEATURES](state, monitoringFeatures) {
      state.monitoringFeatures = monitoringFeatures
    },
    [ADD_MONITORING_FEATURE](state, monitoringFeature) {
      state.monitoringFeatures.push(monitoringFeature)
    },
    [REMOVE_MONITORING_FEATURE](state, monitoringFeature) {
      console.log('remopve', monitoringFeature)
      let index = state.monitoringFeatures.findIndex(mf => mf.Id == monitoringFeature.Id)
      if (index < 0) throw `Could not find monitoring feature id ${monitoringFeature.Id}`
      state.monitoringFeatures.splice(index, 1)
    },
    [SET_FEATURE_DATA_SOURCES](state, featureDataSources) {
      state.featureDataSources = featureDataSources
    },
    [UPDATE_MONITORING_FEATURE](state, monitoringFeature) {
      let index = state.monitoringFeatures.findIndex(mf => mf.Id == monitoringFeature.Id)
      if (index < 0) return

      let mfUpdate = {
        ...state.monitoringFeatures[index],
        ...monitoringFeature,
      }

      state.monitoringFeatures.splice(index, 1, mfUpdate)
    },
    [UPDATE_CURRENT_MONITORING_FEATURE](state, monitoringFeature) {
      if (!state.currentMonitoringFeature || state.currentMonitoringFeature.Id != monitoringFeature.Id) return

      state.currentMonitoringFeature = {
        ...state.currentMonitoringFeature,
        ...monitoringFeature,
      }
    },
    [SET_CURRENT_MONITORING_FEATURE](state, {
      monitoringFeature,
      monitoringFeatureDetails,
    }) {
      state.currentMonitoringFeature = {
        ...monitoringFeature,
        ...(monitoringFeatureDetails || {}),
      }
    },
    [RESET](state) {
      state.configurations = initialState.configurations
    },
  },
  getters: {
    // begin monitoring features
    monitoringFeatures: (state) => state.monitoringFeatures,
    monitoringFeaturesIndex: (state) => {
      let index = {}
      state.monitoringFeatures.forEach(mf => index[mf.Id] = mf)
      return index
    },
    getSourceName: (state, getters, rootState, rootGetters) => (featureDataSource) => {
      if (!featureDataSource) return ''
      let sourceType = featureDataSource.SourceType
      let id = featureDataSource.SourceId

      let dataSourceIndex = rootGetters['datasource/dataSourceIndex']
      let dataSource = dataSourceIndex[sourceType] && dataSourceIndex[sourceType][id]
      if (!dataSource) {
        console.error('Could not match name from from source because source was not found.', featureDataSource)
        return STATION_NOT_FOUND
      }

      return dataSource.LocationIdentifier
    },
    getDisplayName: () => (monitoringFeature) => {
      if (!monitoringFeature) return ''
      return monitoringFeature.DisplayName || NO_LOCATION_NAME
    },
    getDisplayNameById: (_, getters) => (monitoringFeatureId) => {
      if (!monitoringFeatureId) return INVALID_LOCATION
      let monitoringFeature = getters.monitoringFeaturesIndex[monitoringFeatureId]
      if (!monitoringFeature) return LOCATION_NOT_FOUND
      return getters.getDisplayName(monitoringFeature)
    },
    // end monitoring features

    // begin feature data sources
    featureDataSources: (state) => state.featureDataSources,
    featureDataSourceIndexByTypeAndId: (state) => {
      let index = {}
      state.featureDataSources.forEach(fds => {
        if (index[fds.SourceType]) index[fds.SourceType][fds.SourceId] = fds
        else index[fds.SourceType] = {
          [fds.SourceId]: fds,
        }
      })
      return index
    },
    featureDataSourceIndexByMF: (state) => {
      let index = {}
      state.featureDataSources.forEach(fds => {
        if (index[fds.MonitoringFeatureId]) index[fds.MonitoringFeatureId].push(fds)
        else index[fds.MonitoringFeatureId] = [fds]
      })
      return index
    },
    featureDataSourcesIndexByType: (state) => {
      let index = {}
      state.featureDataSources.forEach(fds => {
        if (index[fds.SourceType]) index[fds.SourceType].push(fds)
        else index[fds.SourceType] = [fds]
      })
      return index
    },
    // end feature data sources

    // begin current monitoring feature
    currentMonitoringFeature: state => state.currentMonitoringFeature,
    currentFeatureDataSources: (_, getters) => {
      if (!getters.currentMonitoringFeature) return []
      return getters.currentMonitoringFeature.FeatureDataSources
    },
    currentFeatureDataSourceIndex: (_, getters) => {
      let index = {}
      getters.currentFeatureDataSources.forEach(dataSource =>
        index[dataSource.SourceId] = dataSource)

      return index
    },
    currentFeatureDataSourceIndexByType: (_, getters) => {
      let index = {}
      getters.currentFeatureDataSources.forEach(dataSource => (
        index[dataSource.SourceType] = dataSource))
      return index
    },
    currentFeatureDataSourceData: (_, getters) => (sourceType) => {
      let dataSource = getters.currentFeatureDataSourceIndexByType[sourceType]
      if (dataSource) return dataSource.Data
    },
    // end current monitoring feature
  },
}